import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Button, ButtonGroup } from "react-bootstrap";

import {
  CategoryApiResponse,
  CategoryUpsertRequest,
  fetchCategory,
  upsertCategory,
  deleteCategory,
  upsertCategoryIcon,
} from "../../../../api/categoryApi";
import {
  SubCategoryApiResponse,
  fetchSubCategory,
  SubCategoryUpsertRequest,
  upsertSubCategory,
  deleteSubCategory,
  upsertSubCategoryIcon,
} from "../../../../api/subCategoryApi";
import { PAGE_TITLE, OPERATION_NAME } from "../../../../constants";
import { Operation } from "../../../../types";
import { TableContent, Table } from "../..";
import { uploadImage } from "../../../../api/imageUploadApi";

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 5px;
`;

type CategoryEditFormData =
  | (CategoryUpsertRequest & { icon?: string | null })
  | (SubCategoryUpsertRequest & { icon?: string | null });

const CategoryType = {
  Category: "category",
  SubCategory: "subCategory",
};

const CategoryList = () => {
  const [categoryData, setCategoryData] = useState<CategoryApiResponse[]>([]);
  const [subCategoryData, setSubCategoryData] = useState<SubCategoryApiResponse[]>([]);
  const [editFormData, setEditFormData] = useState<CategoryEditFormData | null>(null);
  const [categoryType, setCategoryType] = useState<string>(CategoryType.Category);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [operationName, setOperationName] = useState<Operation | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [previousOrderIndex, setPreviousOrderIndex] = useState<number | null>(null);
  const [iconData, setIconData] = useState<File | null>(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState<string | ArrayBuffer | null>(null);

  useEffect(() => {
    fetchCategory().then((categoryData) => {
      setCategoryData(categoryData);
    });
    fetchSubCategory().then((subCategoryData) => {
      setSubCategoryData(subCategoryData);
    });
  }, [operationName]);

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    // NOTE: フォームのデフォルト動作を防止
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    if (formData.get("categoryType") === CategoryType.Category) {
      const newItemData: CategoryUpsertRequest = {
        categoryName: formData.get("categoryName") as string,
        orderIndex: Number(formData.get("orderIndex") as string) || null,
        bgColor: (formData.get("bgColor") as string) || null,
      };

      try {
        const newItem = await upsertCategory(newItemData);

        if (newItem && iconData) {
          const iconUrl = await uploadImage({ file: iconData, path: "icons/category" });
          if (iconUrl) await upsertCategoryIcon({ id: newItem.id, icon: iconUrl });
        }
      } catch (error) {
        alert("Create error: " + error);
      }
    } else if (formData.get("categoryType") === CategoryType.SubCategory) {
      const subCategoryReqObj: SubCategoryUpsertRequest = {
        subCategoryName: formData.get("subCategoryName") as string,
        orderIndex: Number(formData.get("orderIndex") as string) || null,
        bgColor: (formData.get("bgColor") as string) || null,
        categoryId: formData.get("parentCategory") as string,
      };

      try {
        const newItem = await upsertSubCategory(subCategoryReqObj);

        if (newItem && iconData) {
          const iconUrl = await uploadImage({ file: iconData, path: "icons/subCategory" });
          if (iconUrl) await upsertSubCategoryIcon({ id: newItem.id, icon: iconUrl });
        }
      } catch (error) {
        alert("Create error: " + error);
      }
    }

    closeModal();
  };

  const handleEditClick = (item: CategoryUpsertRequest | SubCategoryUpsertRequest) => {
    openModal(OPERATION_NAME.EDIT);
    if ("categoryName" in item) {
      setCategoryType(CategoryType.Category);
      setEditFormData({
        id: item.id,
        categoryName: item.categoryName,
        orderIndex: item.orderIndex || null,
        bgColor: item.bgColor || null,
      });
    } else if ("subCategoryName" in item) {
      setCategoryType(CategoryType.SubCategory);
      setEditFormData({
        id: item.id,
        subCategoryName: item.subCategoryName,
        orderIndex: item.orderIndex || null,
        bgColor: item.bgColor || null,
        categoryId: item.categoryId,
      });
    }
    setPreviousOrderIndex(item.orderIndex);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    // NOTE: フォームのデフォルト動作を防止
    event.preventDefault();

    if (!editFormData) return;

    try {
      if (categoryType === CategoryType.Category) {
        const editItemData: CategoryUpsertRequest = {
          ...(editFormData as CategoryUpsertRequest),
          orderIndex: editFormData.orderIndex || null,
        };

        const updatedItem = await upsertCategory(editItemData);
        if (updatedItem && iconData) {
          const iconUrl = await uploadImage({ file: iconData, path: "icons/category" });
          if (iconUrl) await upsertCategoryIcon({ id: updatedItem.id, icon: iconUrl });
        }
      } else if (categoryType === CategoryType.SubCategory) {
        const editItemData: SubCategoryUpsertRequest = {
          ...(editFormData as SubCategoryUpsertRequest),
          orderIndex: editFormData.orderIndex || null,
        };

        const updatedItem = await upsertSubCategory(editItemData);
        if (updatedItem && iconData) {
          const iconUrl = await uploadImage({ file: iconData, path: "icons/subCategory" });
          if (iconUrl) await upsertSubCategoryIcon({ id: updatedItem.id, icon: iconUrl });
        }
      }

      setEditFormData(null);
      closeModal();
    } catch (error) {
      alert("Update error: " + error);
    }
  };

  const handleDeleteClick = async (id: string, type: string) => {
    setOperationName(OPERATION_NAME.DELETE);

    try {
      if (confirm("Are you sure you want to delete this item?")) {
        if (type === CategoryType.Category) {
          const deleteResult = await deleteCategory({ id });
          if (deleteResult) alert("Deleted category successfully");
        } else if (type === CategoryType.SubCategory) {
          const deleteResult = await deleteSubCategory({ id });
          if (deleteResult) alert("Deleted subCategory successfully");
        }
      }
    } catch (error) {
      alert("Delete error:" + error);
    }

    setOperationName(null);
  };

  const handleIconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // NOTE: フォームのデフォルト動作を防止
    event.preventDefault();

    const reader = new FileReader();
    const file = event.target.files?.[0] || null;
    if (file) {
      setIconData(file);
      reader.onloadend = () => {
        setIconPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const openModal = (operation: Operation) => {
    setOperationName(operation);
    setShowModal(true);
  };

  const closeModal = () => {
    setOperationName(null);
    setPreviousOrderIndex(null);
    setSelectedCategoryId(null);
    setIconData(null);
    setIconPreviewUrl(null);
    setShowModal(false);
  };

  return (
    <>
      <h2>{PAGE_TITLE.CategoryListPage}</h2>
      <div className="my-3">
        <Button
          variant="primary"
          onClick={() => {
            openModal(OPERATION_NAME.CREATE);
            setCategoryType(CategoryType.Category);
          }}
        >
          新規作成
        </Button>
      </div>
      <TableContent>
        <Table>
          <thead>
            <tr>
              <th>Icon</th>
              <th>Category Name</th>
              <th>Sub Category Name</th>
              <th>Order Index</th>
              <th>Bg Color</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {categoryData
              .sort((a, b) => (a.orderIndex || Infinity) - (b.orderIndex || Infinity))
              .map((categoryItem) => (
                <React.Fragment key={categoryItem.id}>
                  <tr>
                    <td>
                      <img src={categoryItem.icon || "/noImage.jpg"} width={50} />
                    </td>
                    <td>{categoryItem.categoryName}</td>
                    <td></td>
                    <td>{categoryItem.orderIndex || "--"}</td>
                    <td style={{ backgroundColor: categoryItem.bgColor }}>
                      {categoryItem.bgColor || "--"}
                    </td>
                    <td className="text-center">
                      {new Date(categoryItem.createdAt).toLocaleDateString()}
                    </td>
                    <td className="text-center">
                      {new Date(categoryItem.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="text-center">
                      <ButtonGroup>
                        <Button variant="primary" onClick={() => handleEditClick(categoryItem)}>
                          編集
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => handleDeleteClick(categoryItem.id, CategoryType.Category)}
                        >
                          削除
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                  {subCategoryData
                    .sort((a, b) => (a.orderIndex || Infinity) - (b.orderIndex || Infinity))
                    .map((subCategoryItem) => {
                      if (subCategoryItem.categoryId === categoryItem.id) {
                        return (
                          <tr key={subCategoryItem.id}>
                            <td>
                              {"-- "}
                              <img src={subCategoryItem.icon || "/noImage.jpg"} width={35} />
                            </td>
                            <td></td>
                            <td>{subCategoryItem.subCategoryName}</td>
                            <td>-- {subCategoryItem.orderIndex || "--"}</td>
                            <td style={{ backgroundColor: subCategoryItem.bgColor }}>
                              {subCategoryItem.bgColor || "--"}
                            </td>
                            <td className="text-center">
                              {new Date(subCategoryItem.createdAt).toLocaleDateString()}
                            </td>
                            <td className="text-center">
                              {new Date(subCategoryItem.updatedAt).toLocaleDateString()}
                            </td>
                            <td className="text-center">
                              <ButtonGroup>
                                <Button
                                  variant="primary"
                                  onClick={() => handleEditClick(subCategoryItem)}
                                >
                                  編集
                                </Button>
                                <Button
                                  variant="secondary"
                                  onClick={() =>
                                    handleDeleteClick(subCategoryItem.id, CategoryType.SubCategory)
                                  }
                                >
                                  削除
                                </Button>
                              </ButtonGroup>
                            </td>
                          </tr>
                        );
                      }
                      return null;
                    })}
                </React.Fragment>
              ))}
          </tbody>
        </Table>
      </TableContent>
      {showModal && operationName === OPERATION_NAME.CREATE ? (
        <ModalBackdrop>
          <ModalContent>
            <form onSubmit={handleCreate}>
              <select
                name="categoryType"
                value={categoryType}
                onChange={(e) => setCategoryType(e.target.value)}
              >
                <option value={CategoryType.Category}>Category</option>
                <option value={CategoryType.SubCategory}>Subcategory</option>
              </select>
              {categoryType === CategoryType.SubCategory ? (
                <select
                  name="parentCategory"
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                >
                  <option value="">紐づけ先のCategoryを選択</option>
                  {categoryData.map((category, index) => (
                    <option key={index} value={category.id}>
                      {category.categoryName}
                    </option>
                  ))}
                </select>
              ) : null}
              {categoryType === CategoryType.Category ? (
                <input type="text" name="categoryName" placeholder="Category Name" />
              ) : (
                <input type="text" name="subCategoryName" placeholder="Sub Category Name" />
              )}
              <select name="orderIndex">
                <option value="">表示順を選択</option>
                {(() => {
                  if (categoryType === CategoryType.SubCategory && selectedCategoryId === null)
                    return;
                  const options =
                    categoryType === CategoryType.Category
                      ? categoryData
                          .filter(({ orderIndex }) => orderIndex !== null)
                          .map((category) => (
                            <option key={category.id} value={category.orderIndex}>
                              {category.orderIndex}
                            </option>
                          ))
                      : subCategoryData
                          .filter(
                            (subCategory) =>
                              subCategory.categoryId === selectedCategoryId &&
                              subCategory.orderIndex !== null,
                          )
                          .map((subCategory) => (
                            <option key={subCategory.id} value={subCategory.orderIndex}>
                              {subCategory.orderIndex}
                            </option>
                          ));
                  options.push(
                    <option key={"new"} value={options.length + 1}>
                      {options.length + 1}
                    </option>,
                  );

                  return options;
                })()}
              </select>
              <input type="text" name="bgColor" placeholder="Bg Color (color code)" />
              <button type="submit">Save</button>
              <button type="button" onClick={closeModal}>
                Cancel
              </button>
              <input type="file" name="icon" onChange={handleIconChange} />
              {iconPreviewUrl && <img src={iconPreviewUrl.toString()} width={100} />}
            </form>
          </ModalContent>
        </ModalBackdrop>
      ) : null}
      {showModal && operationName === OPERATION_NAME.EDIT && editFormData ? (
        <ModalBackdrop>
          <ModalContent>
            <form onSubmit={handleSave}>
              {"categoryName" in editFormData ? (
                <>
                  <input
                    type="text"
                    name="categoryName"
                    placeholder="Category Name"
                    value={editFormData.categoryName}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        categoryName: e.target.value,
                      })
                    }
                  />
                  <select
                    name="orderIndex"
                    value={editFormData.orderIndex || ""}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        orderIndex: Number(e.target.value),
                      })
                    }
                  >
                    <option value="">表示順を選択</option>
                    {(() => {
                      const options = categoryData
                        .filter(({ orderIndex }) => orderIndex !== null)
                        .map((category) => (
                          <option key={category.id} value={category.orderIndex}>
                            {category.orderIndex}
                          </option>
                        ));

                      if (previousOrderIndex === null) {
                        options.push(
                          <option key={editFormData.id} value={options.length + 1}>
                            {options.length + 1}
                          </option>,
                        );
                      }

                      return options;
                    })()}
                  </select>
                </>
              ) : null}
              {"subCategoryName" in editFormData ? (
                <>
                  <select
                    name="parentCategory"
                    value={editFormData.categoryId}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        categoryId: e.target.value,
                      })
                    }
                  >
                    {categoryData.map((category, index) => (
                      <option key={index} value={category.id}>
                        {category.categoryName}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    name="subCategoryName"
                    placeholder="Sub Category Name"
                    value={editFormData.subCategoryName}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        subCategoryName: e.target.value,
                      })
                    }
                  />
                  <select
                    name="orderIndex"
                    value={editFormData.orderIndex || ""}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        orderIndex: Number(e.target.value),
                      })
                    }
                  >
                    <option value="">表示順を選択</option>
                    {(() => {
                      const filteredSubCategoryData = subCategoryData.filter(
                        (subCategory) =>
                          subCategory.categoryId === editFormData.categoryId &&
                          subCategory.orderIndex !== null,
                      );

                      const options = filteredSubCategoryData.map((subCategory) => (
                        <option key={subCategory.id} value={subCategory.orderIndex}>
                          {subCategory.orderIndex}
                        </option>
                      ));

                      if (previousOrderIndex === null) {
                        options.push(
                          <option key={editFormData.id} value={options.length + 1}>
                            {options.length + 1}
                          </option>,
                        );
                      }

                      return options;
                    })()}
                  </select>
                </>
              ) : null}
              <input
                type="text"
                name="bgColor"
                placeholder="Bg Color (color code)"
                value={editFormData.bgColor || ""}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    bgColor: e.target.value,
                  })
                }
              />
              <button type="submit">Save</button>
              <button
                type="button"
                onClick={() => {
                  closeModal();
                  setEditFormData(null);
                }}
              >
                Cancel
              </button>
              <input type="file" name="icon" onChange={handleIconChange} />
              <img src={iconPreviewUrl?.toString() || editFormData.icon || ""} width={100} />
            </form>
          </ModalContent>
        </ModalBackdrop>
      ) : null}
    </>
  );
};

export default CategoryList;
